'use client'

import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import "@/styles/primary-button.css"

import { cn } from "@/lib/utils"

const primaryButtonVariants = cva(
    "group relative inline-flex items-center justify-center font-semibold transition-all duration-200",
    {
        variants: {
            variant: {
                blue: "btn-variant-blue text-white md:w-[178px] w-full rounded-full text-base leading-none",
                dark: "btn-variant-dark text-white md:w-[178px] w-auto min-w-[200px] rounded-full text-base leading-none",
                glass: "btn-variant-glass text-gray-1000 flex items-center justify-center min-w-[200px]",
                'glass-black': "btn-variant-glass-black text-gray-1000 flex items-center justify-center min-w-[200px]",
                light: "btn-variant-light text-gray-1000/90 text-sm/5 leading-[.28px] font-normal py-3.5 xl:py-2.5 px-7.5 xl:px-5",
                white: "btn-variant-white text-gray-700 text-sm/5 leading-[.28px] font-normal py-2.5 px-5",
                black: "btn-variant-black text-gray-0/90 text-sm/5 leading-[.28px] font-normal py-2.5 px-5",
                gradient: "relative inline-flex items-center z-10",
                'gradient-black': "relative inline-flex items-center z-10",
            },
            size: {
                default: "px-4 py-2 has-[>svg]:px-3 lg:h-11 h-10 lg:w-[199px]",
                sm: "h-8 rounded-lg gap-1.5 px-3 has-[>svg]:px-2.5",
                lg: "h-10 rounded-lg px-6 has-[>svg]:px-4",
            },
        },
        defaultVariants: {
            variant: "blue",
            size: "default",
        },
    }
)

interface PrimaryButtonProps extends React.ComponentProps<"button">, VariantProps<typeof primaryButtonVariants> {
    asChild?: boolean
}

const PrimaryButton = React.forwardRef<HTMLButtonElement, PrimaryButtonProps>(
    ({ className, variant, size, asChild = false, children, ...props }, ref) => {
        const Comp = asChild ? Slot : "button"

        // Mouse tracking state for gradient variant
        const [mousePosition, setMousePosition] = React.useState({ x: 114.199, y: 0 })
        const [isHovered, setIsHovered] = React.useState(false)
        const timeoutRef = React.useRef<NodeJS.Timeout | undefined>(undefined)
        const buttonRef = React.useRef<HTMLElement>(null)

        // Combine refs
        const combinedRef = React.useCallback((node: HTMLButtonElement | HTMLElement | null) => {
            if (node) {
                buttonRef.current = node
            }
            if (typeof ref === 'function') {
                ref(node as HTMLButtonElement)
            } else if (ref) {
                ref.current = node as HTMLButtonElement
            }
        }, [ref])

        // Mouse event handlers for gradient variant
        const handleMouseMove = React.useCallback((e: React.MouseEvent) => {
            if (variant !== "gradient" && variant !== "gradient-black" || !buttonRef.current) return

            const rect = buttonRef.current.getBoundingClientRect()
            const x = e.clientX - rect.left - rect.width / 2

            setMousePosition({ x, y: 0 })
        }, [variant])

        const handleMouseEnter = React.useCallback(() => {
            if (variant !== "gradient" && variant !== "gradient-black") return

            setIsHovered(true)
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }, [variant])

        const handleMouseLeave = React.useCallback(() => {
            if (variant !== "gradient" && variant !== "gradient-black") return

            setIsHovered(false)
            timeoutRef.current = setTimeout(() => {
                setMousePosition({ x: 114.199, y: 0 })
            }, 1000)
        }, [variant])

        // Cleanup timeout on unmount
        React.useEffect(() => {
            return () => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current)
                }
            }
        }, [])

        // Render Blue Variant
        if (variant === "blue") {
            return (
                <>
                    <svg width="0" height="0" style={{ position: 'absolute' }}>
                        <defs>
                            <filter id="noiseFilter">
                                <feTurbulence type="fractalNoise" baseFrequency="6.29" numOctaves="2" stitchTiles="stitch" />
                                <feColorMatrix type="saturate" values="0" />
                            </filter>
                        </defs>
                    </svg>
                    <Comp
                        ref={ref}
                        className={cn(primaryButtonVariants({ variant, size }), className)}
                        {...props}
                    >
                        <span className="btn-blue-wrapper absolute -top-px -left-px h-full w-full" aria-hidden="true">
                            <span className="btn-blue-background" />
                            <span className="btn-blue-noise" />
                            <span className="btn-blue-hover group-hover:!opacity-100 absolute inset-0 h-full w-full overflow-hidden" />
                        </span>
                        <span className="absolute top-0 left-0 z-20 h-full w-full blur-[1px]" aria-hidden="true">
                            <span className="btn-blue-border absolute -top-px -left-px z-20 h-full w-full" />
                        </span>
                        <span className="absolute -inset-px h-full w-full overflow-hidden rounded-full" aria-hidden="true">
                            <span className="absolute -top-[52px] right-1 size-20 rounded-[100%] bg-[#7DF0F8] opacity-40 mix-blend-lighten blur-[30px]" />
                            <span className="absolute top-[0px] right-1 h-[28px] w-20 rounded-[100%] bg-[#7DF0F8] opacity-40 mix-blend-lighten blur-[30px]" />
                            <span className="absolute -bottom-[42px] -left-[19px] size-20 rounded-[100%] bg-[#7DF0F8] opacity-40 mix-blend-lighten blur-[30px]" />
                        </span>
                        <span className="relative z-10 flex items-center gap-1.5 text-14 leading-none -tracking-[0.04em] lg:text-base">
                            {children}
                        </span>
                    </Comp>
                </>
            )
        }

        // Render Dark Variant
        if (variant === "dark") {
            return (
                <Comp
                    ref={ref}
                    className={cn(primaryButtonVariants({ variant, size }), className)}
                    {...props}
                >
                    <div className="absolute size-full overflow-hidden">


                        <div
                            className={cn(
                                "h-3 bg-radial-[ellipse_at_center] to-transparent w-1/2 absolute -bottom-1 left-1/2 -translate-x-1/2 z-10",
                                "transition-all duration-500",
                                "from-theos-500/100 via-theos-500/0",
                                "group-hover:w-2/3 group-focus-visible:w-2/3 group-hover:h-7 group-hover:-bottom-4",
                                "group-active:w-full group-active:duration-75 group-active:h-10 group-active:-bottom-6",
                                "group-disabled:opacity-0"
                            )}
                        />
                        <div
                            className={cn(
                                "h-4 bg-radial-[ellipse_at_bottom] to-transparent w-full absolute -bottom-px z-10 left-1/2 -translate-x-1/2",
                                "transition-all duration-500",
                                "from-theos-500/80 via-theos-500/0",
                                "group-hover:from-theos-500/50 group-focus-visible:from-theos-400/50 group-hover:h-7",
                                "group-active:from-theos-500/60 group-active:duration-75 group-active:h-10",
                                "group-disabled:opacity-0"
                            )}
                        />
                    </div>
                    <span className="btn-dark-wrapper absolute -top-[1px] -left-[1px] h-full w-full" aria-hidden="true">
                        <span className="btn-dark-hover group-hover:!opacity-100 absolute inset-0 h-full w-full overflow-hidden" />
                    </span>
                    <span className="absolute top-0 left-0 z-20 h-full w-full blur-[1px]" aria-hidden="true">
                        <span className="btn-dark-border absolute -top-[1px] -left-[1px] z-20 h-full w-full" />
                    </span>
                    <span className="relative z-10 flex items-center gap-1.5 text-14 leading-none -tracking-[0.04em] lg:text-base [&_svg]:size-5 [&_svg]:lg:size-[26px]">
                        {children}
                    </span>
                </Comp>
            )
        }

        // Render Glass Variant
        if (variant === "glass") {
            return (
                <>
                    <svg width="0" height="0" style={{ position: 'absolute' }}>
                        <defs>
                            <filter id="noiseFilter">
                                <feTurbulence type="fractalNoise" baseFrequency="6.29" numOctaves="2" stitchTiles="stitch" />
                                <feColorMatrix type="saturate" values="0" />
                            </filter>
                        </defs>
                    </svg>
                    <Comp
                        ref={ref}
                        className={cn(primaryButtonVariants({ variant, size }), className)}
                        {...props}
                    >
                        <div className="absolute inset-0 rounded-[inherit] transition-opacity duration-100 bg-white opacity-0 will-change-[opacity] group-hover:opacity-100" />

                        <span className="relative z-10 flex items-center gap-1.5 text-14 leading-none -tracking-[0.04em] lg:text-base [&_svg]:size-5 [&_svg]:lg:size-[26px] text-gray-1000 text-center">
                            {children}
                        </span>

                        <div
                            className={cn(
                                "h-3 bg-radial-[ellipse_at_center] to-transparent w-1/2 absolute -bottom-1 left-1/2 -translate-x-1/2 z-10",
                                "transition-all duration-500",
                                "from-theos-500/100 via-theos-500/0",
                                "group-hover:w-2/3 group-focus-visible:w-2/3",
                                "group-active:w-full group-active:duration-75",
                                "group-disabled:opacity-0"
                            )}
                        />
                        <div
                            className={cn(
                                "h-4 bg-radial-[ellipse_at_bottom] to-transparent w-full absolute -bottom-px z-10 left-1/2 -translate-x-1/2",
                                "transition-all duration-500",
                                "from-theos-500/80 via-theos-500/0",
                                "group-hover:from-theos-500/50 group-focus-visible:from-theos-500/50",
                                "group-active:from-theos-500/60 group-active:duration-75",
                                "group-disabled:opacity-0"
                            )}
                        />
                    </Comp>
                </>
            )
        }

        if (variant === "glass-black") {
            return (
                <>
                    <svg width="0" height="0" style={{ position: 'absolute' }}>
                        <defs>
                            <filter id="noiseFilter">
                                <feTurbulence type="fractalNoise" baseFrequency="6.29" numOctaves="2" stitchTiles="stitch" />
                                <feColorMatrix type="saturate" values="0" />
                            </filter>
                        </defs>
                    </svg>
                    <Comp
                        ref={ref}
                        className={cn(primaryButtonVariants({ variant, size }), className)}
                        {...props}
                    >
                        <div className="absolute inset-0 rounded-[inherit] transition-opacity duration-100 bg-white opacity-0 will-change-[opacity] group-hover:opacity-100" />

                        <span className="relative z-10 flex items-center gap-1.5 text-14 leading-none -tracking-[0.04em] lg:text-base [&_svg]:size-5 [&_svg]:lg:size-[26px] text-gray-1000 text-center">
                            {children}
                        </span>

                        <div
                            className={cn(
                                "h-3 bg-radial-[ellipse_at_center] to-transparent w-1/2 absolute -bottom-1 left-1/2 -translate-x-1/2 z-10",
                                "transition-all duration-500",
                                "from-theos-500/100 via-theos-500/0",
                                "group-hover:w-2/3 group-focus-visible:w-2/3",
                                "group-active:w-full group-active:duration-75",
                                "group-disabled:opacity-0"
                            )}
                        />
                        <div
                            className={cn(
                                "h-4 bg-radial-[ellipse_at_bottom] to-transparent w-full absolute -bottom-px z-10 left-1/2 -translate-x-1/2",
                                "transition-all duration-500",
                                "from-theos-500/80 via-theos-500/0",
                                "group-hover:from-theos-500/50 group-focus-visible:from-theos-500/50",
                                "group-active:from-theos-500/60 group-active:duration-75",
                                "group-disabled:opacity-0"
                            )}
                        />
                    </Comp>
                </>
            )
        }


        // Render Light Variant
        if (variant === "light") {
            return (
            //     <div className="flex items-center relative rounded-full pl-4 xl:pl-2 group h-12 xl:h-10 w-27 pr-1"><div className="overlay pointer-events-none">
            //         <div className="overlay" data-theme="morning" >
            //             <div className="overlay header-button-overlay-background transition">
            //             </div>
            //             <div className="overlay header-button-overlay-background transition">
            //             </div>
            //             <div className="overlay header-button-overlay-background transition">
            //             </div>
            //             <div className="overlay header-button-overlay-background transition">
            //             </div>
            //             <div className="overlay header-button-overlay-border mask-border p-px">
            //             </div>
            //             <div className="overlay header-button-overlay-shadow">
            //             </div>
            //         </div>
            //     </div>
            // </div>
                <Comp
                    ref={ref}
                    className={cn(primaryButtonVariants({ variant, size }), className)}
                    {...props}
                >
                    <div className="absolute top-0 left-0 w-full h-full rounded-full pointer-events-none">
                        <div className="absolute top-0 left-0 w-full h-full rounded-full" data-theme="morning">
                            <div className="absolute top-0 left-0 w-full h-full rounded-full btn-light-overlay-1 transition" />
                            <div className="absolute top-0 left-0 w-full h-full rounded-full btn-light-overlay-2 transition" />
                            <div className="absolute top-0 left-0 w-full h-full rounded-full btn-light-overlay-3 transition" />
                            <div className="absolute top-0 left-0 w-full h-full rounded-full btn-light-overlay-4 transition" />
                            <div className="absolute top-0 left-0 w-full h-full rounded-full btn-light-border mask-border p-px" />
                            <div className="absolute top-0 left-0 w-full h-full rounded-full btn-light-shadow" />
                        </div>
                    </div>
                    <span className="relative z-10">{children}</span>
                </Comp>

            )
        }

        if (variant === "gradient") {
            return (
                <Comp
                    ref={combinedRef}
                    className={cn(primaryButtonVariants({ variant, size }), className)}
                    onMouseMove={handleMouseMove}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    {...props}
                >
                    <div className="border-button-light-blur !absolute left-1/2 top-1/2 h-[calc(100%+9px)] w-[calc(100%+9px)] -translate-x-1/2 -translate-y-1/2 rounded-full will-change-transform" style={{ opacity: 1 }}>
                        <div className="border-button-light relative h-full w-full rounded-full">
                        </div>
                    </div>
                    <div className="border-button-light-blur !absolute left-1/2 top-1/2 h-[calc(100%+9px)] w-[calc(100%+9px)] -translate-x-1/2 -translate-y-1/2 scale-x-[-1] transform rounded-full will-change-transform" style={{ opacity: 1 }}>
                        <div className="border-button-light relative h-full w-full rounded-full">
                        </div>
                    </div>
                    <a className="transition-all duration-200 uppercase font-bold flex items-center justify-center h-10 px-16 text-xs text-black -tracking-[0.015em] relative z-10 overflow-hidden rounded-full border border-white/60 bg-[#d1d1d1] space-x-1 sm:pl-[59px] sm:pr-[52px]" href="https://front.hc.engineering/workbench/platform">
                        <div
                            className="absolute -z-10 flex w-[204px] items-center justify-center transition-transform duration-200 ease-out"
                            style={{
                                transform: `translateX(${mousePosition.x}px) translateZ(0px)`
                            }}
                        >
                            <div className="absolute top-1/2 h-[121px] w-[121px] -translate-y-1/2 bg-[radial-gradient(50%_50%_at_50%_50%,#FFFFF5_3.5%,_#FFAA81_26.5%,#FFDA9F_37.5%,rgba(255,170,129,0.50)_49%,rgba(210,106,58,0.00)_92.5%)]">
                            </div>
                            <div className="absolute top-1/2 h-[103px] w-[204px] -translate-y-1/2 bg-[radial-gradient(43.3%_44.23%_at_50%_49.51%,_#FFFFF7_29%,_#FFFACD_48.5%,_#F4D2BF_60.71%,rgba(214,211,210,0.00)_100%)] blur-[5px]">
                            </div>
                        </div>
                        <span className="text-[#5A250A]">
                            {children}
                        </span>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 17 9" className="h-[9px] w-[17px] text-[#5A250A]">
                            <path fill="currentColor" fillRule="evenodd" d="m12.495 0 4.495 4.495-4.495 4.495-.99-.99 2.805-2.805H0v-1.4h14.31L11.505.99z" clipRule="evenodd">
                            </path>
                        </svg>
                    </a>
                </Comp>
            )
        }

        if (variant === "gradient-black") {
            return (
                <Comp
                    ref={combinedRef}
                    className={cn(primaryButtonVariants({ variant, size }), className)}
                    onMouseMove={handleMouseMove}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    {...props}
                >
                    <div className="border-button-light-blur !absolute left-1/2 top-1/2 h-[calc(100%+9px)] w-[calc(100%+9px)] -translate-x-1/2 -translate-y-1/2 rounded-full will-change-transform" style={{ opacity: 1 }}>
                        <div className="border-button-light relative h-full w-full rounded-full">
                        </div>
                    </div>
                    <div className="border-button-light-blur !absolute left-1/2 top-1/2 h-[calc(100%+9px)] w-[calc(100%+9px)] -translate-x-1/2 -translate-y-1/2 scale-x-[-1] transform rounded-full will-change-transform" style={{ opacity: 1 }}>
                        <div className="border-button-light relative h-full w-full rounded-full">
                        </div>
                    </div>
                    <a className="transition-all duration-200 uppercase font-bold flex items-center justify-center h-10 px-16 text-xs text-white -tracking-[0.015em] relative z-10 overflow-hidden rounded-full border border-black/20 bg-[#0a0a0a] space-x-1 sm:pl-[59px] sm:pr-[52px]" href="https://front.hc.engineering/workbench/platform">
                        <div
                            className="absolute -z-10 flex w-[204px] items-center justify-center transition-transform duration-200 ease-out"
                            style={{
                                transform: `translateX(${mousePosition.x}px) translateZ(0px)`
                            }}
                        >
                            <div className="absolute top-1/2 h-[121px] w-[121px] -translate-y-1/2 bg-[radial-gradient(50%_50%_at_50%_50%,hsl(220_100%_96%)_3.5%,hsl(223_90%_80%)_26.5%,hsl(224_85%_72%)_37.5%,hsla(225_75%_63%/0.50)_49%,hsla(224_60%_55%/0.00)_92.5%)]">
                            </div>
                            <div className="absolute top-1/2 h-[103px] w-[204px] -translate-y-1/2 bg-[radial-gradient(43.3%_44.23%_at_50%_49.51%,hsl(221_100%_93%)_29%,hsl(222_95%_87%)_48.5%,hsl(223_90%_80%)_60.71%,hsla(220_100%_96%/0.00)_100%)] blur-[5px]">
                            </div>
                        </div>
                        <span className="text-white">
                            {children}
                        </span>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 17 9" className="h-[9px] w-[17px] text-white">
                            <path fill="currentColor" fillRule="evenodd" d="m12.495 0 4.495 4.495-4.495 4.495-.99-.99 2.805-2.805H0v-1.4h14.31L11.505.99z" clipRule="evenodd">
                            </path>
                        </svg>
                    </a>
                </Comp>
            )
        }



        // Render White Variant
        if (variant === "white") {
            return (
                <Comp
                    ref={ref}
                    className={cn(primaryButtonVariants({ variant, size }), className)}
                    {...props}
                >
                    <div className="btn-white-background">
                        <div className="btn-white-overlay-1" />
                        <div className="btn-white-overlay-2" />
                        <div className="btn-white-overlay-3" />
                        <div className="btn-white-overlay-4" />
                        <div className="btn-white-border" />
                    </div>
                    <div className="relative z-10">
                        {children}
                    </div>
                </Comp>
            )
        }

        // Render Black Variant
        if (variant === "black") {
            return (
                <Comp
                    ref={ref}
                    className={cn(primaryButtonVariants({ variant, size }), className)}
                    {...props}
                >
                    <div className="btn-black-background">
                        <div className="btn-black-overlay-1" />
                        <div className="btn-black-overlay-2" />
                        <div className="btn-black-border" />
                    </div>
                    <div className="relative z-10">
                        {children}
                    </div>
                </Comp>
            )
        }

        // Default fallback (should not happen with proper typing)
        return (
            <Comp
                ref={ref}
                className={cn(primaryButtonVariants({ variant, size }), className)}
                {...props}
            >
                {children}
            </Comp>
        )
    }
)

PrimaryButton.displayName = "PrimaryButton"

export { PrimaryButton, primaryButtonVariants }
export type { PrimaryButtonProps }